<template lang="pug">
  .yz-btn-toggle(:class="toggleClasses" :style="toggleStyles")
    button.yz-btn-toggle__button(
      v-for="(item, index) in items"
      :key="getItemValue(item, index)"
      :class="getButtonClasses(item, index)"
      :style="getButtonStyles(item, index)"
      :disabled="disabled || item.disabled"
      @click="handleClick(item, index)"
      type="button"
    )
      .yz-btn-toggle__content
        .yz-btn-toggle__label(:class="getLabelClass()") {{ getItemName(item) }}
</template>

<script>
export default {
  name: 'YzBtnToggle',
  props: {
    value: {
      type: [String, Number, Array],
      default: null
    },
    items: {
      type: Array,
      required: true,
      validator: (items) => {
        return items.every(item => {
          return typeof item === 'string' || typeof item === 'number' || (typeof item === 'object' && (item.name || item.label))
        })
      }
    },
    mandatory: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    radius: {
      type: [String, Number],
      default: '10px'
    },
    color: {
      type: String,
      default: 'white'
    },
    activeColor: {
      type: String,
      default: 'neutral-300'
    },
    textColor: {
      type: String,
      default: 'neutral-500'
    },
    activeTextColor: {
      type: String,
      default: 'neutral-700'
    },
    borderColor: {
      type: String,
      default: 'neutral-400'
    },
    activeBorderColor: {
      type: String,
      default: 'neutral-700'
    },
    hoverColor: {
      type: String,
      default: 'neutral-200'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    dense: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    },
    itemText: {
      type: String,
      default: 'name'
    },
    itemValue: {
      type: String,
      default: 'value'
    }
  },
  computed: {
    toggleClasses() {
      return {
        'yz-btn-toggle--disabled': this.disabled,
        'yz-btn-toggle--dense': this.dense,
        'yz-btn-toggle--block': this.block
      }
    },
    toggleStyles() {
      const styles = {}

      // Handle radius
      if (this.radius) {
        styles.borderRadius = typeof this.radius === 'number' ? `${this.radius}px` : this.radius
      }

      return styles
    }
  },
  methods: {
    isSelected(item, index) {
      const itemValue = this.getItemValue(item, index)
      if (this.multiple && Array.isArray(this.value)) {
        return this.value.includes(itemValue)
      }
      return this.value === itemValue
    },
    getItemValue(item, index) {
      if (typeof item === 'object' && item !== null) {
        return item[this.itemValue] !== undefined ? item[this.itemValue] : index
      }
      return item
    },
    getItemName(item) {
      if (typeof item === 'object' && item !== null) {
        return item[this.itemText] || item.label || item.name || ''
      }
      return String(item)
    },
    getButtonClasses(item, index) {
      return {
        'yz-btn-toggle__button--active': this.isSelected(item, index),
        'yz-btn-toggle__button--disabled': this.disabled || item.disabled,
        'yz-btn-toggle__button--first': index === 0,
        'yz-btn-toggle__button--last': index === this.items.length - 1
      }
    },
    getButtonStyles(item, index) {
      const styles = {}
      const isActive = this.isSelected(item, index)

      // Background color - use CSS variable to allow hover to work
      const bgColor = isActive ? this.activeColor : this.color
      styles['--bg-color'] = this.parseColor(bgColor)

      // Border color - use CSS variable
      const bColor = isActive ? this.activeBorderColor : this.borderColor
      styles['--border-color'] = this.parseColor(bColor)

      // Text color - set as CSS variable for label to use
      const tColor = isActive ? this.activeTextColor : this.textColor
      styles['--text-color'] = this.parseColor(tColor)

      // Hover color
      styles['--hover-color'] = this.parseColor(this.hoverColor)

      return styles
    },
    getLabelClass() {
      return {
        'yz-medium-body-sm': !this.dense,
        'yz-medium-body-xs': this.dense
      }
    },
    parseColor(color) {
      if (!color) return null

      // Handle hex colors
      if (color.startsWith('#')) {
        return color
      }

      // Handle CSS variables
      if (color.startsWith('var(')) {
        return color
      }

      // Handle YDS color names
      if (color.startsWith('yz-')) {
        return `var(--${color})`
      }

      // Default: assume it's a YDS color without prefix
      return `var(--yz-${color})`
    },
    handleClick(item, index) {
      if (this.disabled || (item && item.disabled)) {
        return
      }

      const itemValue = this.getItemValue(item, index)

      if (this.multiple) {
        let newValue = Array.isArray(this.value) ? [...this.value] : []
        const valueIndex = newValue.indexOf(itemValue)

        if (valueIndex > -1) {
          // Deselect - check if mandatory
          if (this.mandatory && newValue.length === 1) {
            return
          }
          newValue.splice(valueIndex, 1)
        } else {
          // Select
          newValue.push(itemValue)
        }

        this.$emit('input', newValue)
        this.$emit('change', newValue)
      } else {
        // Single selection
        if (this.mandatory && this.value === itemValue) {
          // Don't allow deselection if mandatory
          return
        }

        const newValue = this.value === itemValue ? null : itemValue
        this.$emit('input', newValue)
        this.$emit('change', newValue)
      }
    }
  }
}
</script>

<style scoped>
.yz-btn-toggle {
  display: inline-flex;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  width: auto;
}

.yz-btn-toggle--block {
  width: 100%;
}

.yz-btn-toggle--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.yz-btn-toggle__button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border: 1px solid;
  border-color: var(--border-color);
  background-color: var(--bg-color);
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
  outline: none;
  position: relative;
  margin-right: -1px;
  min-height: 36px;
}

.yz-btn-toggle--dense .yz-btn-toggle__button {
  padding: 4px 12px;
  min-height: 28px;
}

/* Border radius for first and last buttons */
.yz-btn-toggle__button--first {
  border-top-left-radius: inherit;
  border-bottom-left-radius: inherit;
}

.yz-btn-toggle__button--last {
  border-top-right-radius: inherit;
  border-bottom-right-radius: inherit;
  margin-right: 0;
}

.yz-btn-toggle__button:hover:not(:disabled):not(.yz-btn-toggle__button--disabled):not(.yz-btn-toggle__button--active) {
  background-color: var(--hover-color);
  z-index: 1;
}

.yz-btn-toggle__button--active {
  z-index: 2;
  font-weight: 600;
}

.yz-btn-toggle__button:disabled,
.yz-btn-toggle__button--disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.yz-btn-toggle__content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.yz-btn-toggle__label {
  color: var(--text-color);
  white-space: nowrap;
  text-transform: none;
  font-weight: 600;
  transition: color 0.2s ease;
}

/* Focus styles for accessibility */
.yz-btn-toggle__button:focus-visible {
  outline: 2px solid var(--yz-neutral-700);
  outline-offset: -2px;
  z-index: 3;
}

/* Prevent text selection on rapid clicks */
.yz-btn-toggle__button {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
</style>
